import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

const DEFAULT_CODE_LENGTH = 6

type Props = {
  onCodeChange: (code: string) => void
  label?: string
  disabled?: boolean
  autoFocus?: boolean
  length?: number
}

const OtpCodeInput = ({
  onCodeChange,
  label = 'הזן את הקוד מהאפליקציה:',
  disabled = false,
  autoFocus = true,
  length = DEFAULT_CODE_LENGTH,
}: Props) => {
  const CODE_LENGTH = length
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const inputRefs = useRef<HTMLInputElement[]>([])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, idx: number): void => {
      const val = e.target.value.replace(/\D/, '')
      if (!val) return

      const next = [...digits]
      next[idx] = val.slice(-1)
      setDigits(next)
      onCodeChange(next.join(''))

      if (idx < CODE_LENGTH - 1) {
        inputRefs.current[idx + 1]?.focus()
      }
    },
    [digits, onCodeChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, idx: number): void => {
      if (e.key === 'Backspace') {
        const next = [...digits]
        if (next[idx]) {
          next[idx] = ''
          setDigits(next)
          onCodeChange(next.join(''))
        } else if (idx > 0) {
          inputRefs.current[idx - 1]?.focus()
        }
      }
    },
    [digits, onCodeChange]
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>): void => {
      e.preventDefault()
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
      const next = Array(CODE_LENGTH).fill('')
      pasted.split('').forEach((ch, i) => { next[i] = ch })
      setDigits(next)
      onCodeChange(next.join(''))
      const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1)
      inputRefs.current[focusIdx]?.focus()
    },
    [onCodeChange]
  )

  const handleClear = useCallback((): void => {
    const empty = Array(CODE_LENGTH).fill('')
    setDigits(empty)
    onCodeChange('')
    inputRefs.current[0]?.focus()
  }, [onCodeChange])

  return (
    <OtpSection>
      <OtpLabel>{label}</OtpLabel>
      <OtpInputRow>
        {Array.from({ length: CODE_LENGTH }).map((_, idx) => (
          <OtpInput
            key={idx}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={digits[idx]}
            ref={(el) => { if (el) inputRefs.current[idx] = el }}
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            $isFilled={!!digits[idx]}
            disabled={disabled}
            aria-label={`ספרה ${idx + 1}`}
            autoFocus={autoFocus && idx === 0}
          />
        ))}
      </OtpInputRow>
      <ClearBtn
        tabIndex={0}
        role="button"
        aria-label="נקה קוד"
        onClick={handleClear}
        onKeyDown={(e) => e.key === 'Enter' && handleClear()}
      >
        נקה
      </ClearBtn>
    </OtpSection>
  )
}

const OtpSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8em;
  width: 100%;
`

const OtpLabel = styled.p`
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  direction: rtl;
`

const OtpInputRow = styled.div`
  display: flex;
  gap: clamp(0.3em, 1.5vw, 0.6em);
  justify-content: center;
  width: 100%;
  direction: ltr;
`

const OtpInput = styled.input<{ $isFilled: boolean }>`
  width: min(2.8em, calc((100% - 5 * clamp(0.3em, 1.5vw, 0.6em)) / 6));
  aspect-ratio: 0.85 / 1;
  min-width: 0;
  text-align: center;
  font-size: clamp(1rem, 4vw, 1.4rem);
  font-weight: 700;
  border-radius: ${({ theme }) => theme.radii.md};
  border: 1px solid
    ${({ $isFilled, theme }) => ($isFilled ? theme.colors.ink : theme.colors.line)};
  background: ${({ $isFilled, theme }) =>
    $isFilled ? theme.colors.accentSoft : theme.colors.surface};
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  transition: border-color 0.2s, background 0.2s, transform 0.1s, box-shadow 0.2s;
  caret-color: transparent;
  flex-shrink: 1;

  &:focus {
    border-color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
    transform: scale(1.04);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ClearBtn = styled.span`
  font-size: 0.88rem;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: tomato; }
`

export default OtpCodeInput
